import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { SettingsCard, ConfirmationDialog } from '../ui';
import { usePasswordVerification } from '../hooks';
import { ExtendedBusinessDetails } from '../utils';

interface BusinessDeletionSectionProps {
  businessDetails: ExtendedBusinessDetails;
  onDeleteSuccess: () => void;
}

export const BusinessDeletionSection: React.FC<BusinessDeletionSectionProps> = ({
  businessDetails,
  onDeleteSuccess,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { password, setPassword, isVerifying, resetVerification } = usePasswordVerification();

  const handleDeleteBusiness = async () => {
    if (!password.trim()) {
      toast.error("Please enter your password to confirm deletion.");
      return;
    }

    setIsDeleting(true);
    try {
      // Verify password first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast.error("Unable to verify user. Please try logging in again.");
        return;
      }

      // Verify password by attempting to sign in
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: password
      });

      if (authError) {
        toast.error("Incorrect password. Please try again.");
        setPassword("");
        return;
      }

      // If password is correct, proceed with deletion
      const { error } = await supabase.functions.invoke('delete-business', {
        body: { business_id: businessDetails.id }
      });

      if (error) {
        console.error('Error deleting business:', error);
        toast.error("Failed to delete business. Please try again.");
        return;
      }

      toast.success("Business deleted successfully.");
      onDeleteSuccess();
    } catch (error) {
      console.error('Error during business deletion:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsDeleting(false);
      resetVerification();
    }
  };

  const handleCancelDelete = () => {
    resetVerification();
  };

  const deletionDescription = (
    <>
      <p>
        <strong>This action cannot be undone.</strong> This will permanently delete your business
        and remove all data from our servers.
      </p>
      <ul className="list-disc list-inside space-y-1 text-sm mt-4">
        <li>All your businesses and their data</li>
        <li>All employee accounts created under your businesses</li>
        <li>All reviews, passes, and customer data</li>
        <li>All shift schedules and time tracking data</li>
        <li>All loyalty programs and rewards</li>
      </ul>
    </>
  );

  return (
    <SettingsCard 
      icon={Trash2} 
      title="Danger Zone" 
      description="Permanently delete this business and all associated data. This action cannot be undone."
      variant="destructive"
    >
      <ConfirmationDialog
        trigger={
          <Button variant="destructive" className="w-full">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Business
          </Button>
        }
        title="Confirm Business Deletion"
        description={deletionDescription}
        confirmText={isDeleting ? "Deleting..." : "Delete Business"}
        cancelText="Cancel"
        onConfirm={handleDeleteBusiness}
        onCancel={handleCancelDelete}
        variant="destructive"
        requiresPassword={true}
        password={password}
        onPasswordChange={setPassword}
        loading={isDeleting || isVerifying}
        disabled={!password.trim()}
      />
    </SettingsCard>
  );
};